import {
	Button,
	Card,
	ControlledCheckbox,
	ControlledDropdown,
	ControlledInput,
	ControlledPhoneInput,
	DataTable,
	RadioGroup,
	RadioGroupItem,
} from "@dune/ui";
import "@dune/ui/globals.css";
import { FileWarning } from "lucide-react";
import {
	Control,
	Controller,
	FieldErrors,
	UseFormHandleSubmit,
	UseFormWatch,
} from "react-hook-form";
import { RegisterSecondStepDetailsFormData } from "./UseRegisterSecondStepBroker";
import { categoriesColumns } from "./categoriesColumns";
import {
	howDidYouKnowDuneChoice,
	PositionInTheCompanyChoice,
} from "@dune/types";

interface RegisterPart1Props {
	control: Control<RegisterSecondStepDetailsFormData>;
	errors: FieldErrors<RegisterSecondStepDetailsFormData>;
	watch: UseFormWatch<RegisterSecondStepDetailsFormData>;
	handleSubmit: UseFormHandleSubmit<RegisterSecondStepDetailsFormData>;
	onSubmit: (data: RegisterSecondStepDetailsFormData) => void;
	isLoading: boolean;
}

export default function CompleteInformations({
	control,
	errors,
	watch,
	handleSubmit,
	onSubmit,
	isLoading,
}: RegisterPart1Props) {
	const isSignatory = watch("isSignatory");

	return (
		<form
			onSubmit={handleSubmit(onSubmit)}
			className="max-w-[950px] w-full mx-auto bg-white p-8"
		>
			<div className="flex-1 p-6 space-y-6">
				<div className="flex flex-col gap-1">
					<div className="text-2xl text-text-main-blue">
						Bienvenue sur la plateforme DUNE
					</div>
					<div className="text-xl">
						Vérifiez et complétez les informations ci-dessous
					</div>
				</div>
				<div className="flex flex-col gap-4 pb-3">
					<div className="text-text-main-blue  font-semibold">
						Informations société
					</div>
					<Card className="p-6 flex flex-col gap-4 overflow-auto text-center">
						<div className="flex flex-row gap-4">
							<div className="flex flex-col items-start gap-3 w-full">
								<div className="text-text-secondary-blue">Numéro ORIAS</div>
								<ControlledInput
									control={control}
									name="oriasNumber"
									disabled
								/>
							</div>
							<div className="flex flex-col items-start gap-3 w-full">
								<div className="text-text-secondary-blue">SIREN</div>
								<ControlledInput control={control} name="siren" disabled />
							</div>
						</div>
						<div className="flex flex-col items-start gap-3">
							<div className="text-text-secondary-blue">Dénomination</div>
							<ControlledInput control={control} name="denomination" disabled />
						</div>
						<div className="flex flex-col items-start gap-3">
							<div className="text-text-secondary-blue">
								Adresse du siège social
							</div>
							<ControlledInput
								control={control}
								name="headquartersAddress"
								disabled
							/>
						</div>
						<div className="flex flex-col items-start gap-3">
							<div className="text-text-secondary-blue">Catégories</div>
							<DataTable
								columns={categoriesColumns}
								data={watch("categories")}
								noResultsText="Aucune catégorie trouvée"
								icon={FileWarning}
								variant="gray-no-pagination"
								isLoading={isLoading}
							/>
						</div>
					</Card>
				</div>

				<div className="border-t border-gray-300 my-4" />

				<div className="pt-4 pb-3">
					<div className="justify-self-start text-text-main-blue font-semibold">
						Informations utilisateur
					</div>
					<div className="text-sm justify-self-start mb-4 text-gray-500">
						Complétez les informations relatives à votre profil utilisateur
					</div>
					<Card className="p-6 flex flex-col gap-4 overflow-auto">
						<div className="flex flex-row gap-4">
							<div className="flex flex-col items-start gap-3 w-full">
								<div className="text-text-secondary-blue">Nom*</div>
								<ControlledInput
									control={control}
									name="lastname"
									error={errors.lastname?.message?.toString()}
								/>
							</div>
							<div className="flex flex-col items-start gap-3 w-full">
								<div className="text-text-secondary-blue">Prénom*</div>
								<ControlledInput
									control={control}
									name="firstname"
									error={errors.firstname?.message?.toString()}
								/>
							</div>
						</div>
						<div className="flex flex-row gap-4">
							<div className="flex flex-col items-start gap-3 w-full">
								<div className="text-text-secondary-blue">Email*</div>
								<ControlledInput
									control={control}
									name="email"
									error={errors.email?.message?.toString()}
								/>
							</div>
							<div className="flex flex-col items-start gap-3 w-full">
								<div className="text-text-secondary-blue">
									Fonction dans l'entreprise*
								</div>
								<ControlledDropdown
									name="position"
									control={control}
									options={PositionInTheCompanyChoice}
								/>
								{errors.position && (
									<span className="text-red-500 text-sm">
										{errors.position.message?.toString()}
									</span>
								)}
							</div>
						</div>
						<div className="flex flex-row gap-4">
							<div className="flex flex-col items-start gap-3 w-full">
								<div className="text-text-secondary-blue">Téléphone fixe</div>
								<ControlledPhoneInput
									control={control}
									name="landlinePhone"
									placeholder="Ex : +33 1 23 45 67 89"
									defaultCountry="FR"
								/>
								{errors.landlinePhone && (
									<span className="text-red-500 text-sm">
										{errors.landlinePhone.message?.toString()}
									</span>
								)}
							</div>
							<div className="flex flex-col items-start gap-3 w-full">
								<div className="text-text-secondary-blue">Mobile*</div>
								<ControlledPhoneInput
									control={control}
									name="phone"
									placeholder="Ex : +33 6 12 34 56 78"
									defaultCountry="FR"
								/>
								{errors.phone && (
									<span className="text-red-500 text-sm">
										{errors.phone.message?.toString()}
									</span>
								)}
							</div>
						</div>
						<div className="flex flex-col gap-2">
							<div className="text-text-secondary-blue">
								Etes-vous habilité à signer la convention de partenariat ?*
							</div>
							<Controller
								name="isSignatory"
								control={control}
								defaultValue={true}
								render={({ field }) => (
									<RadioGroup
										value={field.value ? "true" : "false"}
										onValueChange={(value) => {
											field.onChange(value === "true");
										}}
										className="flex flex-row justify-between"
									>
										<div className="flex items-center space-x-2 w-full">
											<div className="flex flex-row">
												<RadioGroupItem value="true" id="r1" fillInside />
												<label htmlFor="r1">Oui</label>
											</div>
										</div>
										<div className="flex items-center space-x-2 w-full">
											<div className="flex flex-row">
												<RadioGroupItem value="false" id="r2" fillInside />
												<label htmlFor="r2">Non</label>
											</div>
										</div>
									</RadioGroup>
								)}
							/>
						</div>
					</Card>
				</div>

				{!isSignatory && (
					<>
						<div className="border-t border-gray-300 my-4" />

						<div className="pt-4">
							<div className="justify-self-start text-text-main-blue pb-4 font-semibold">
								Signataire
							</div>
							<div>
								<Card className="p-6 flex flex-col gap-4 overflow-auto text-center">
									<div className="flex flex-row gap-4">
										<div className="flex flex-col items-start gap-3 w-full">
											<div className="text-text-secondary-blue">Nom*</div>
											<ControlledInput
												control={control}
												name="lastnameSignatory"
												error={errors.lastnameSignatory?.message?.toString()}
											/>
										</div>
										<div className="flex flex-col items-start gap-3 w-full">
											<div className="text-text-secondary-blue">Prénom*</div>
											<ControlledInput
												control={control}
												name="firstnameSignatory"
												error={errors.firstnameSignatory?.message?.toString()}
											/>
										</div>
									</div>
									<div className="flex flex-row gap-4">
										<div className="flex flex-col items-start gap-3 w-full">
											<div className="text-text-secondary-blue">Email*</div>
											<ControlledInput
												control={control}
												name="emailSignatory"
												error={errors.emailSignatory?.message?.toString()}
											/>
										</div>
										<div className="flex flex-col items-start gap-3 w-full">
											<div className="text-text-secondary-blue">
												Fonction dans l'entreprise*
											</div>
											<ControlledDropdown
												name="positionSignatory"
												control={control}
												options={PositionInTheCompanyChoice}
											/>
											{errors.positionSignatory && (
												<span className="text-red-500 text-sm">
													{errors.positionSignatory.message?.toString()}
												</span>
											)}
										</div>
									</div>
									<div className="flex flex-row gap-4">
										<div className="flex flex-col items-start gap-3 w-full">
											<div className="text-text-secondary-blue">
												Téléphone fixe
											</div>
											<ControlledPhoneInput
												control={control}
												name="landlinePhoneSignatory"
												placeholder="Ex : +33 1 23 45 67 89"
												defaultCountry="FR"
											/>

											{errors.landlinePhoneSignatory && (
												<span className="text-red-500 text-sm">
													{errors.landlinePhoneSignatory.message?.toString()}
												</span>
											)}
										</div>
										<div className="flex flex-col items-start gap-3 w-full">
											<div className="text-text-secondary-blue">Mobile*</div>
											<ControlledPhoneInput
												control={control}
												name="phoneSignatory"
												placeholder="Ex : +33 6 12 34 56 78"
												defaultCountry="FR"
											/>
											{errors.phoneSignatory && (
												<span className="text-red-500 text-sm">
													{errors.phoneSignatory.message?.toString()}
												</span>
											)}
										</div>
									</div>
								</Card>
							</div>
						</div>
					</>
				)}

				<div className="border-t border-gray-300 my-4" />

				<div className="flex flex-col gap-10">
					<div className="flex flex-col gap-2 pt-4">
						<div className="text-text-secondary-blue font-semibold">
							Comment avez-vous connu DUNE ?*
						</div>
						<ControlledDropdown
							name="howDidYouKnowDune"
							control={control}
							options={howDidYouKnowDuneChoice}
						/>
						{errors.howDidYouKnowDune && (
							<span className="text-red-500 text-sm">
								{errors.howDidYouKnowDune.message?.toString()}
							</span>
						)}
					</div>
					<div className="flex flex-row gap-2">
						<ControlledCheckbox
							control={control}
							className="text-text-secondary-blue font-semibold"
							id="newsletter"
							name="newsletter"
							label="Je ne souhaite pas recevoir les informations relatives aux
										actualités, évolutions et nouvelles offres proposées par
										DUNE"
						/>
					</div>
				</div>

				<div className="flex justify-end">
					<Button type="submit" className="h-11">
						Suivant
					</Button>
				</div>
			</div>
		</form>
	);
}
